import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import SignInProducer from './producer/signin-producer';
import SignInPresenter from './signin-presenter';
import PresenterLounge from '../../images/green-room-presenter-no-red-chair-gray.jpeg';
import AppEntryVideoView from './left-panels/app-entry-video-view';
import CreateAccount from './create-account';
import Onboarding from './onboarding';
import IPhonePreview from './left-panels/iphone-preview';
import ConfirmAppEntry from './confirm-app-entry';
import { ONBOARDING_ROUTES } from '../../stores/session-status';
import ProducerControlRoom from '../../images/green-room-producer-no-red-chair.png';
import RedOfficeChair from '../../images/red-office-chair.png';
import RedBeanChair from '../../images/red-bean-chair.png';
import Socket from '../../connection/socket';
import WaitingIndicator from '../general-components/waiting-indicator';
import ForgotPassword from './forgot-password';
import PasswordReset from './password-reset';
import Clock from './left-panels/clock';
import TellyRight1 from '../../images/telly-right-1.png';
import TellyRight2 from '../../images/telly-right-2.png';
import TellyRight3 from '../../images/telly-right-3.png';
import TellyLeft1 from '../../images/telly-left-1.png';
import TellyLeft2 from '../../images/telly-left-2.png';
import TellyLeft3 from '../../images/telly-left-3.png';
import HomeContainer from './home/home-container';
import TellyChannelLogo from '../../images/app-store-icon.png';
import ProducerSelectApp from './producer/producer-select-app';
import { GreenroomState } from '../../stores/types/types';
import Icon, { ICONS } from '../general-components/icon';
import * as ModalActions from '../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';

const IntroVid = require('../../images/brandlive.mp4');

interface Props {
	// props that will be passed to this component
	socket: Socket | null;
	connected: boolean;
}

const PRESENTER_ROUTES = [
	ONBOARDING_ROUTES.PRESENTER_LOGIN,
	ONBOARDING_ROUTES.PRESENTER_CREATE_ACCOUNT,
	ONBOARDING_ROUTES.PRESENTER_FORGOT_PASSWORD,
	ONBOARDING_ROUTES.PRESENTER_RESET_PASSWORD,
];

const PRODUCER_ROUTES = [
	ONBOARDING_ROUTES.PRODUCER_LOGIN,
	ONBOARDING_ROUTES.PRODUCER_FORGOT_PASSWORD,
	ONBOARDING_ROUTES.PRODUCER_RESET_PASSWORD,
	ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL,
	ONBOARDING_ROUTES.PRODUCER_SELECT_APP,
	ONBOARDING_ROUTES.PRODUCER_CREATE_ACCOUNT,
];

const NONE = [
	ONBOARDING_ROUTES.JOIN_PRESENTER,
	ONBOARDING_ROUTES.PRESENTER_SELECT_MEETING,
	ONBOARDING_ROUTES.PRODUCER_SELECT_MEETING,
	ONBOARDING_ROUTES.PRODUCER_CONFIRM,
];

const PRESENTER_ONBOARDING_ROUTES = [
	ONBOARDING_ROUTES.PRESENTER_LOGIN,
	ONBOARDING_ROUTES.ONBOARDING,
	ONBOARDING_ROUTES.PRESENTER_CREATE_ACCOUNT,
	ONBOARDING_ROUTES.PRESENTER_FORGOT_PASSWORD,
	ONBOARDING_ROUTES.PRESENTER_RESET_PASSWORD,
];

const SHARED_ONBOARDING_HEADER = [
	ONBOARDING_ROUTES.PRESENTER_LOGIN,
	ONBOARDING_ROUTES.ONBOARDING,
	ONBOARDING_ROUTES.PRESENTER_CREATE_ACCOUNT,
	ONBOARDING_ROUTES.PRESENTER_FORGOT_PASSWORD,
	ONBOARDING_ROUTES.PRESENTER_RESET_PASSWORD,
	ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL,
	ONBOARDING_ROUTES.PRODUCER_LOGIN,
	ONBOARDING_ROUTES.PRODUCER_SELECT_APP,
	ONBOARDING_ROUTES.PRODUCER_CREATE_ACCOUNT,
	ONBOARDING_ROUTES.PRODUCER_FORGOT_PASSWORD,
	ONBOARDING_ROUTES.PRODUCER_RESET_PASSWORD,
];

export default function AppEntry(props: Props) {
	const {
		socket,
		connected,
	} = props;

	const onboardingRoute = useSelector(
		(state: GreenroomState) => state.SessionReducer.onboarding_route,
	);

	const dispatch = useDispatch();
	const onboardingTab = useSelector((state: GreenroomState) => state.SessionReducer.onboardingTab);
	const meetingModalOpen = useSelector(
		(state: GreenroomState) => state.SessionReducer.meetingModalOpen,
	);
	const useMobile = useSelector((state: GreenroomState) => state.VideoReducer.useMobile);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);

	const [iconSize, setIconSize] = useState(
		window.innerWidth * 0.02,
	);
	const [showPlay, setShowPlay] = useState(false);

	const currentRoute = useMemo(() => {
		switch (onboardingRoute) {
			case ONBOARDING_ROUTES.JOIN_PRESENTER: {
				return (
					<WaitingIndicator />
				);
			}

			case ONBOARDING_ROUTES.PRESENTER_LOGIN: {
				return (
					<SignInPresenter />
				);
			}

			case ONBOARDING_ROUTES.PRESENTER_CREATE_ACCOUNT:
			case ONBOARDING_ROUTES.PRODUCER_CREATE_ACCOUNT: {
				return (
					<CreateAccount />
				);
			}

			case ONBOARDING_ROUTES.ONBOARDING: {
				return (
					<Onboarding socket={socket} />
				);
			}

			case ONBOARDING_ROUTES.PRODUCER_ENTER_EMAIL:
			case ONBOARDING_ROUTES.PRODUCER_LOGIN: {
				return (
					<SignInProducer />
				);
			}

			case ONBOARDING_ROUTES.PRODUCER_SELECT_APP: {
				return (
					<ProducerSelectApp />
				);
			}

			case ONBOARDING_ROUTES.PRODUCER_SELECT_MEETING: {
				return <HomeContainer connected={connected} socket={socket} />;
			}

			case ONBOARDING_ROUTES.PRODUCER_CONFIRM: {
				return <ConfirmAppEntry />;
			}

			case ONBOARDING_ROUTES.PRESENTER_SELECT_MEETING: {
				return <HomeContainer connected={connected} socket={socket} />;
			}

			case ONBOARDING_ROUTES.PRESENTER_FORGOT_PASSWORD: {
				return <ForgotPassword />;
			}

			case ONBOARDING_ROUTES.PRESENTER_RESET_PASSWORD: {
				return <PasswordReset />;
			}

			case ONBOARDING_ROUTES.PRODUCER_FORGOT_PASSWORD: {
				return <ForgotPassword host={true} />;
			}

			case ONBOARDING_ROUTES.PRODUCER_RESET_PASSWORD: {
				return <PasswordReset host={true} />;
			}

			default: {
				return (
					<SignInPresenter />
				);
			}
		}
	}, [onboardingRoute, socket, connected, useMobile]);

	const playVideoTutorial = () => {
		dispatch(ModalActions.setModalState({
			modalOpen: true,
			modalTemplate: MODAL_TEMPLATES.PRESENTER_ONBOARDING_TUTORIAL_VIDEO,
		}));
	};

	useEffect(() => {
		window.addEventListener('resize', () => {
			setIconSize(
				window.innerWidth * 0.02,
			);
		});
		const videoElement = document.getElementById('intro-vid');
		if (videoElement) {
			videoElement.addEventListener('ended', () => setShowPlay(true));
		}
	}, []);

	useEffect(() => {
		if (
			(user && Number(user.tutorial_video_count) < 1)
			&& ONBOARDING_ROUTES.ONBOARDING === onboardingRoute
		) {
			playVideoTutorial();
		}
	}, [user]);

	const leftPanel = useMemo(() => {
		if (
			PRESENTER_ROUTES.includes(onboardingRoute)
			|| (PRESENTER_ONBOARDING_ROUTES.includes(onboardingRoute) && onboardingTab < 1)
		) {
			return (
				<div className="image-container presenter">
					<div className="image-full-height">
						<div className="image-wrapper">
							<img alt={'The Brandlive Presenter Lounge'} src={PresenterLounge} />
							<img className="red-bean-chair" alt={'A Red Beanbag'} src={RedBeanChair} />
							<div className="intro-vid-wrapper">
								<video
									id="intro-vid"
									className="intro-vid"
									muted={true}
									autoPlay={true}
								>
									<source src={IntroVid} type="video/mp4"></source>
								</video>
								<div className="triangle">
									<svg width="100%" height="100%" fill="#ffffff" fillOpacity={'20%'} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
										<polygon points="0 0, 100 100, 0 100" />
									</svg>
								</div>
								{showPlay && user && (
									<div className="intro-vid-play">
										<div className="intro-vid-play-cell" onClick={playVideoTutorial}>
											<div className="intro-vid-play-icon">
												<Icon name={ICONS.PLAY} size={iconSize} color="white" />
											</div>
											<div className="intro-vid-play-text">
												Getting started
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			);
		} if (useMobile && onboardingRoute < 3) {
			return <IPhonePreview />;
		} if (PRODUCER_ROUTES.includes(onboardingRoute)) {
			return (
				<div className="image-container producer">
					<div className="image-full-height">
						<div className="image-wrapper">
							<img alt={'The Brandlive Producer Control Room'} src={ProducerControlRoom} />
							<div className="tv-left">
								<img className="tellyLeft1" alt={'Telly Left 1'} src={TellyLeft1} />
								<img className="tellyLeft2" alt={'Telly Left 2'} src={TellyLeft2} />
								<img className="tellyLeft3" alt={'Telly Left 3'} src={TellyLeft3} />
							</div>
							<div className="tv-right">
								<img className="tellyRight1" alt={'Telly Right 1'} src={TellyRight1} />
								<img className="tellyRight2" alt={'Telly Right 2'} src={TellyRight2} />
								<img className="tellyRight3" alt={'Telly Right 3'} src={TellyRight3} />
							</div>
							<img className="red-office-chair" alt={'A Red Office Chair'} src={RedOfficeChair} />
							<Clock />
						</div>
					</div>
				</div>
			);
		} if (NONE.includes(onboardingRoute)) {
			return null;
		}

		return (
			<AppEntryVideoView/>
		);
	}, [
		onboardingTab,
		onboardingRoute,
		useMobile,
		showPlay,
		user,
	]);

	const onboardingHeader: JSX.Element = useMemo(() => {
		if (SHARED_ONBOARDING_HEADER.includes(onboardingRoute)) {
			return (
				<header className="entry-header">
					<div className="telly-channel-bubble-container">
						<div className="telly-channel-bubble" style={{ marginLeft: '24px' }}>
							<img src={TellyChannelLogo} alt='telly'></img>
						</div>
					</div>
				</header>
			);
		}

		return <></>;
	}, [onboardingRoute]);

	return useMemo(() => (
		<>
			<div className={classNames('app-entry', {
				presenter: PRESENTER_ROUTES.includes(onboardingRoute),
				producer: PRODUCER_ROUTES.includes(onboardingRoute),
				full: NONE.includes(onboardingRoute),
			})}>
				{onboardingHeader}
				<div style={{
					zIndex: 'auto',
					overflow: meetingModalOpen ? 'hidden' : '', // This gets rid of having 2 scrollbars (background and modal). When the modal is open we want one scrollbar visible for it.
				}}>
					{currentRoute}
				</div>
				{leftPanel}
			</div>
		</>
	), [onboardingRoute, currentRoute, leftPanel,
		onboardingTab, meetingModalOpen, onboardingHeader]);
}
